/**
 * RACITemplateBrowser
 *
 * Read-only browser for RACI matrix templates. Displays name, description,
 * and a badge showing the number of activities defined in each template.
 *
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { BrowserLayout } from './BrowserLayout';

// ============================================================================
// TYPES
// ============================================================================

interface RACITemplate {
  id: string;
  name: string;
  description?: string;
  activities?: Array<{ name: string }>;
}

interface ApiResponse {
  templates?: RACITemplate[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const RACITemplateBrowser: React.FC = () => {
  const { authFetch } = useApiClient();

  const [templates, setTemplates] = useState<RACITemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch('/api/governance/templates/raci');
        if (!response.ok) {
          throw new Error(`Failed to load RACI templates (HTTP ${response.status})`);
        }
        const data: ApiResponse | RACITemplate[] = await response.json();
        const raw: RACITemplate[] = Array.isArray(data)
          ? data
          : (data as ApiResponse).templates ?? [];
        if (!cancelled) {
          setTemplates(raw);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unexpected error loading templates.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  // ---- Render states -------------------------------------------------------

  if (loading) {
    return (
      <BrowserLayout
        title="RACI Templates"
        description="Pre-built RACI matrices for common governance activities. Each template defines Responsible, Accountable, Consulted, and Informed roles."
      >
        <div className="browser-loading">
          <div className="spinner" />
          Loading RACI templates…
        </div>
      </BrowserLayout>
    );
  }

  if (error) {
    return (
      <BrowserLayout
        title="RACI Templates"
        description="Pre-built RACI matrices for common governance activities. Each template defines Responsible, Accountable, Consulted, and Informed roles."
      >
        <div className="browser-error">
          <div className="error-icon">⚠</div>
          <p>{error}</p>
        </div>
      </BrowserLayout>
    );
  }

  // ---- Main render ---------------------------------------------------------

  return (
    <BrowserLayout
      title="RACI Templates"
      description="Pre-built RACI matrices for common governance activities. Each template defines Responsible, Accountable, Consulted, and Informed roles."
    >
      <div className="template-card-grid">
        {templates.map((template) => {
          const activityCount = template.activities?.length ?? 0;

          return (
            <div key={template.id} className="template-card">
              <h3>{template.name}</h3>
              {template.description && (
                <p className="template-card-description">{template.description}</p>
              )}
              <div className="template-card-badges">
                <span className="badge badge-gray">
                  {activityCount} {activityCount === 1 ? 'activity' : 'activities'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </BrowserLayout>
  );
};

export default RACITemplateBrowser;
